import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { RezaSablonaDto } from '@gov-nx/api';
import { rezaTemplates, useAxiosInstance, usePoQuery } from '@gov-nx/api';
import type { PageCode } from '@gov-nx/module/page';
import type { FormInputProps } from '@gov-nx/ui/types';
import { useFilter } from '@gov-nx/utils/common';
import { useRezaLocale } from '../../useRezaLocale';

interface RezaTemplateFilterForm {
	search?: string;
}

interface RezaTemplateListContext {
	code: PageCode;
	isLoading: boolean;
	isError: boolean;
	templates: RezaSablonaDto[];
	formDefinition: FormDefinition<RezaTemplateFilterForm>;
	onSubmit: () => void;
	fields: {
		search: FormInputProps;
		button: {
			wcagLabel: string;
		};
	};
}

const Context = createContext<RezaTemplateListContext | null>(null);

type Props = PropsWithChildren<{ code: PageCode }>;

export const RezaTemplateListContextProvider = ({ code, children }: Props) => {
	const { t } = useRezaLocale();
	const { filter, setPartialFilter } = useFilter<RezaTemplateFilterForm>({
		key: 'reza-templates',
		initialState: {
			search: undefined,
		},
	});
	const { axiosInstance } = useAxiosInstance();

	const query = usePoQuery({
		queryKey: ['reza', 'templates', filter],
		queryFn: async () =>
			rezaTemplates(axiosInstance, {
				text: filter.search,
			}),
	});

	const formSchema = yup
		.object({
			search: yup.string().optional(),
		})
		.required();

	const formMethods = useGovForm<RezaTemplateFilterForm>({
		formSchema,
		defaultValues: {
			search: filter.search,
		},
	});

	const formDefinition = getFormDefinition({ formMethods, formSchema });

	const onSubmit = formDefinition.formMethods.handleSubmit((values) => {
		setPartialFilter({
			search: values.search,
		});
	});

	return (
		<Context.Provider
			value={{
				code,
				isLoading: query.isLoading,
				isError: query.isError,
				templates: query.data?.seznam ?? [],
				formDefinition,
				onSubmit,
				fields: {
					search: {
						field: {
							name: 'search',
							placeholder: t('sablony.pole.hledat.placeholder'),
						},
						label: { children: t('sablony.pole.hledat.label') },
					},
					button: {
						wcagLabel: t('sablony.buttons.submit.wcag'),
					},
				},
			}}>
			{children}
		</Context.Provider>
	);
};

export const useRezaTemplateListContext = (): RezaTemplateListContext => {
	const context = useContext(Context);
	if (!context) {
		throw new Error(
			'useRezaTemplateListContext must be used within a RezaTemplateListContextProvider'
		);
	}
	return context;
};
